import { useCallback, useState } from 'react';

import { DateTools, TFileWithType } from '@treezor/design-system';

import { EParentType } from '@/features/user/store/user.model';
import { useCreateUser } from '@/features/user/hooks/createUser/useCreateUser';
import { ICreateUserInfo, IPostCreateUser, ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { useMembers } from '@/features/user/hooks/createUser/useMembers';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { kycDefaultValues } from '@/features/user/components/CreateUser/parts/physical/CreateUserPhysicalVerificationDetails';
import { userInformationDefaultValues } from '@/features/user/components/CreateUser/parts/physical/CreateUserPhysicalUserInformation';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';

export const defaultCompanyMember: IPostCreateUser = {
  ...userInformationDefaultValues,
  ...kycDefaultValues,
};

export function useCreateBusinessUser(defaultValues?: IPostCreateUser) {
  const { handleSubmit, language, getValues, onCreateUser, setValue, handleValueChange, user, watches, ...rest } =
    useCreateUser(defaultValues);

  const [userId, setUserId] = useState<number | undefined>();

  const { addTaxResidences } = useAddTaxResidences(userId);
  const { addKycDocuments } = useCreateUserKycDocuments(userId);
  const { addMembers } = useMembers(userId);

  const members: ICreateUserInfo[] = watches['members'];

  const handleChangeMembers = useCallback(
    (
      index: number,
      key: keyof ICreateUserInfo,
      value: string | number | ITaxResidenceCreate[] | TFileWithType[] | `${EParentType}`[],
    ) => {
      const newMembers = [...members];
      newMembers[index] = {
        ...newMembers[index],
        [key]: value,
      };
      handleValueChange(newMembers, 'members');
    },
    [handleValueChange, members],
  );

  const handleChangeMemberProps = useCallback(
    (
      index: number,
      props: {
        key: keyof ICreateUserInfo;
        value: string | ITaxResidenceCreate[] | TFileWithType[] | `${EParentType}`[];
      }[],
    ) => {
      const newMembers = [...members];
      newMembers[index] = {
        ...newMembers[index],
        ...props.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {}),
      };
      handleValueChange(newMembers, 'members');
    },
    [handleValueChange, members],
  );

  const handleDeleteMember = useCallback(
    (indexToDelete: number) => {
      const newMembers = members.filter((_, index) => index !== indexToDelete);
      handleValueChange(newMembers, 'members');
    },
    [handleValueChange, members],
  );

  const onAddMember = useCallback(() => {
    handleValueChange([...members, defaultCompanyMember], 'members');
  }, [handleValueChange, members]);

  const onConfirmCreation = useCallback(() => {
    const {
      legalRegistrationDate,
      entityType,
      taxResidences,
      activityOutsideEu,
      economicSanctions,
      residentCountriesSanctions,
      involvedSanctions,
      specifiedUSPerson,
      kycDocuments,
      members,
      ...rest
    } = user;
    const legalRegistrationDateApiFormat = legalRegistrationDate
      ? DateTools.format(DateTools.localStringToDate(legalRegistrationDate, language), 'api-date')
      : undefined;

    onCreateUser(
      {
        ...rest,
        legalRegistrationDate: legalRegistrationDateApiFormat,
        entityType: Number(user.entityType),
        specifiedUSPerson: Number(specifiedUSPerson),
        activityOutsideEu: Number(activityOutsideEu),
        economicSanctions: Number(economicSanctions),
        residentCountriesSanctions: Number(residentCountriesSanctions),
        involvedSanctions: Number(involvedSanctions),
      },
      undefined,
      (userId: number) => {
        setUserId(userId);
        addTaxResidences(taxResidences, userId);
        addKycDocuments(kycDocuments, userId);
        addMembers(members, userId);
      },
    );
  }, [user, language, onCreateUser, addTaxResidences, addKycDocuments, addMembers]);

  const handleBeforeSubmitMember = useCallback(() => {
    const newMembers = members.map((member) => {
      return member.isSameAsPrincipalAddress === 'yes'
        ? {
            ...member,
            address1: user?.address1 || '',
            address2: user?.address2 || '',
            address3: user?.address3 || '',
            postcode: user?.postcode || '',
            country: user?.country || undefined,
            city: user?.city || '',
          }
        : member;
    });
    handleValueChange(newMembers, 'members');
    handleSubmit();
  }, [
    handleSubmit,
    handleValueChange,
    members,
    user?.address1,
    user?.address2,
    user?.address3,
    user?.city,
    user?.country,
    user?.postcode,
  ]);

  return {
    handleSubmit,
    user,
    userId,
    language,
    members,
    watches,
    onConfirmCreation,
    getValues,
    handleValueChange,
    handleChangeMembers,
    handleDeleteMember,
    onAddMember,
    handleBeforeSubmitMember,
    handleChangeMemberProps,
    ...rest,
  };
}
